export interface VideoTopic {
  id: string;
  label: string;
  filename: string;
  url: string;
  duration: string;
  description: string;
}

export interface VideoSection {
  id: string;
  label: string;
  icon: string;
  description: string;
  topics: VideoTopic[];
}

// Set NEXT_PUBLIC_VIDEO_BASE_URL once videos are hosted on your own server (see deploy/nginx-video-server.conf)
const VIDEO_BASE_URL = process.env.NEXT_PUBLIC_VIDEO_BASE_URL || 'https://v2st.in/bbng';

// Backend integration point: Replace with API call to fetch video library data
export const videoLibraryData: VideoSection[] = [
  {
    id: 'my-profile',
    label: 'My Profile',
    icon: 'UserIcon',
    description: 'Manage your BBNG profile, products, offers, clients and reporting',
    topics: [
      {
        id: 'edit-profile',
        label: 'Edit Profile',
        filename: 'Edit Profile.mp4',
        url: `${VIDEO_BASE_URL}/Edit_Profile.mp4`,
        duration: '4:32',
        description: 'Learn how to update your personal and business profile information on BBNG app.',
      },
      {
        id: 'add-products',
        label: 'Add Products',
        filename: 'Add Products.mp4',
        url: `${VIDEO_BASE_URL}/Add_Products.mp4`,
        duration: '3:18',
        description: 'Step-by-step guide to adding your business products and services to your BBNG profile.',
      },
      {
        id: 'add-offers',
        label: 'Add Offers',
        filename: 'Add Offers.mp4',
        url: `${VIDEO_BASE_URL}/Add_Offers.mp4`,
        duration: '2:55',
        description: 'How to create and publish special offers for BBNG members to see.',
      },
      {
        id: 'add-clients',
        label: 'Add Clients',
        filename: 'Add Clients.mp4',
        url: `${VIDEO_BASE_URL}/Add_Clients.mp4`,
        duration: '3:40',
        description: 'Track your business clients and showcase your client base within BBNG.',
      },
      {
        id: 'report-abuse',
        label: 'Report Abuse',
        filename: 'Report Abuse.mp4',
        url: `${VIDEO_BASE_URL}/Report_Abuse.mp4`,
        duration: '2:10',
        description: 'How to report inappropriate content or behaviour to the BBNG admin team.',
      },
    ],
  },
  {
    id: 'member-sign',
    label: 'Member Sign',
    icon: 'IdentificationIcon',
    description: 'Member registration and login process for new BBNG members',
    topics: [
      {
        id: 'member-sign-up',
        label: 'Member Sign Up',
        filename: 'Member Sign Up.mp4',
        url: `${VIDEO_BASE_URL}/Member_Sign_Up.mp4`,
        duration: '5:14',
        description: 'Complete walkthrough of the BBNG member registration process.',
      },
      {
        id: 'login',
        label: 'Login',
        filename: 'Login.mp4',
        url: `${VIDEO_BASE_URL}/Login.mp4`,
        duration: '1:48',
        description: 'How to sign in to your BBNG member account on the app.',
      },
    ],
  },
  {
    id: 'member-list',
    label: 'Member List',
    icon: 'UsersIcon',
    description: 'Find members, send references, manage deals and meetings',
    topics: [
      {
        id: 'member-filter-search',
        label: 'Member Filter And Search',
        filename: 'Member Filter and Search.mp4',
        url: `${VIDEO_BASE_URL}/Member_Filter_Search.mp4`,
        duration: '3:52',
        description: 'How to filter and search for specific BBNG members by industry, chapter, or name.',
      },
      {
        id: 'sent-references',
        label: 'Sent References',
        filename: 'Sent References.mp4',
        url: `${VIDEO_BASE_URL}/Sent_References.mp4`,
        duration: '4:05',
        description: 'Guide to sending business references to fellow BBNG members.',
      },
      {
        id: 'done-deals',
        label: 'Done Deals',
        filename: 'Done Deals.mp4',
        url: `${VIDEO_BASE_URL}/Done_Deals.mp4`,
        duration: '2:48',
        description: 'How to record and mark completed business deals within BBNG.',
      },
      {
        id: 'sent-one-to-one-meeting',
        label: 'Sent One to One Meeting',
        filename: 'Sent One to One Meeting.mp4',
        url: `${VIDEO_BASE_URL}/Sent_One_to_One_Meeting.mp4`,
        duration: '3:20',
        description: 'How to send a one-to-one meeting request to another BBNG member.',
      },
      {
        id: 'accept-one-to-one-meeting',
        label: 'Accept One to One Meeting',
        filename: 'Accept One to One Meeting.mp4',
        url: `${VIDEO_BASE_URL}/Accept_One_to_One_Meeting.mp4`,
        duration: '2:35',
        description: 'Steps to accept or decline a one-to-one meeting request from another member.',
      },
      {
        id: 'sent-review',
        label: 'Sent Review',
        filename: 'Sent Review.mp4',
        url: `${VIDEO_BASE_URL}/Sent_Review.mp4`,
        duration: '2:58',
        description: 'How to submit a business review for a BBNG member you have worked with.',
      },
    ],
  },
  {
    id: 'group-meeting-admin',
    label: 'Group Meeting For Admin',
    icon: 'PresentationChartBarIcon',
    description: 'Admin tools for creating and managing BBNG group meetings',
    topics: [
      {
        id: 'admin-group-meeting',
        label: 'Group Meeting',
        filename: '(Admin) Add Group Meeting.mp4',
        url: `${VIDEO_BASE_URL}/Admin_Add_Group_Meeting.mp4`,
        duration: '6:10',
        description: 'Admin guide to creating and scheduling a BBNG group meeting.',
      },
      {
        id: 'mark-attendance',
        label: 'Mark Attendance',
        filename: '(Admin) Mark Attendance.mp4',
        url: `${VIDEO_BASE_URL}/Admin_Mark_Attendance.mp4`,
        duration: '4:22',
        description: 'How admins mark and track member attendance at group meetings.',
      },
      {
        id: 'add-visitors',
        label: 'Add Visitors',
        filename: '(Admin) Add Visitor.mp4',
        url: `${VIDEO_BASE_URL}/Admin_Add_Visitor.mp4`,
        duration: '3:05',
        description: 'Adding guest visitors to a BBNG group meeting as an admin.',
      },
      {
        id: 'add-presenters',
        label: 'Add Presenters',
        filename: '(Admin) Add Presenters.mp4',
        url: `${VIDEO_BASE_URL}/Admin_Add_Presenters.mp4`,
        duration: '3:44',
        description: 'How to assign and manage business presenters for a group meeting.',
      },
    ],
  },
  {
    id: 'events',
    label: 'Events',
    icon: 'CalendarIcon',
    description: 'Create and manage BBNG events and workshops',
    topics: [
      {
        id: 'add-events',
        label: 'Add Events',
        filename: 'Add Event (Admin).mp4',
        url: `${VIDEO_BASE_URL}/Add_Event_Admin.mp4`,
        duration: '5:30',
        description: 'Admin guide to creating and publishing a new BBNG event or workshop.',
      },
    ],
  },
  {
    id: 'group-meeting-member',
    label: 'Group Meeting For Member',
    icon: 'UserGroupIcon',
    description: 'Member view for participating in BBNG group meetings',
    topics: [
      {
        id: 'member-group-meeting',
        label: 'Group Meeting',
        filename: 'Group Meeting (Normal Member).mp4',
        url: `${VIDEO_BASE_URL}/Group_Meeting_Normal_Member.mp4`,
        duration: '4:48',
        description: 'How regular members view, join, and participate in BBNG group meetings.',
      },
    ],
  },
  {
    id: 'request-category',
    label: 'Request Category',
    icon: 'TagIcon',
    description: 'Request new business categories for your BBNG profile',
    topics: [
      {
        id: 'request-category',
        label: 'Request Category',
        filename: 'Request Category.mp4',
        url: `${VIDEO_BASE_URL}/Request_Category.mp4`,
        duration: '2:22',
        description: 'How to request a new business category to be added to the BBNG platform.',
      },
    ],
  },
];

export function getTopicById(topicId: string): { topic: VideoTopic; section: VideoSection } | null {
  for (const section of videoLibraryData) {
    const topic = section.topics.find((t) => t.id === topicId);
    if (topic) return { topic, section };
  }
  return null;
}

export function getSectionById(sectionId: string): VideoSection | null {
  return videoLibraryData.find((s) => s.id === sectionId) || null;
}